import { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import DotsLoading from 'components/loadings/dotsLoading'
import {
    IdentityRefusal,
    useMemberIdentity,
    useSetMemberIdentity,
} from './hooks/useMemberIdentity'
import { formOf, identityChanges, IdentityForm } from './identityChanges'
import { blockMessage, inputError, SAVE_FAILED } from './messages'

interface IModalProps {
    show: boolean
    onHide: () => void
    user: IMoneyPoolResults | undefined
    refetch: () => void
}

/**
 * «اصلاح نام و شماره» — a manager correcting what they typed for a member
 * who has never signed in (pwa#149).
 *
 * The backend decides who can be corrected; this only asks and shows the
 * answer. For a member who is no longer the manager's to change it says why
 * instead of offering a form that would be refused.
 */
export default function EditMemberIdentityModal({
    show,
    onHide,
    user,
    refetch,
}: IModalProps) {
    return (
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            {/* The modal drops its children while hidden, so the body mounts
                again on every open and asks the backend again. */}
            {user && (
                <Body
                    key={user.id}
                    user={user}
                    onHide={onHide}
                    refetch={refetch}
                />
            )}
        </Modal>
    )
}

const INPUT =
    ' outline-none border-1 border-gray-5 rounded-md w-full py-2.5 text-center'
const LABEL = ' block text-right text-xs text-gray-1 mb-1 mt-3'

function Body({
    user,
    onHide,
    refetch,
}: {
    user: IMoneyPoolResults
    onHide: () => void
    refetch: () => void
}) {
    const { id } = useParams()
    const {
        data,
        isLoading,
        isError,
        refetch: ask,
    } = useMemberIdentity(id, user.id)
    const { mutate, isLoading: isSaving } = useSetMemberIdentity(refetch)
    const [form, setForm] = useState<IdentityForm | undefined>()
    const [error, setError] = useState<string>('')
    // The member signed in between opening the form and saving it.
    const [refusedReason, setRefusedReason] = useState<string | undefined>()

    useEffect(() => {
        if (!data) return
        setForm(formOf(data))
        posthog.capture('member_identity_edit_opened', {
            fund_id: id,
            poolship_id: user.id,
            editable: data.editable,
            reason: data.reason,
        })
    }, [data, id, user.id])

    const set = (field: keyof IdentityForm) => (value: string) => {
        setForm((old) => (old ? { ...old, [field]: value } : old))
        setError('')
    }

    const save = () => {
        if (!data || !form || isSaving) return
        const parsed = identityChanges(data, form)
        if (!parsed.ok) {
            setError(inputError(parsed.error))
            return
        }
        // Saved as it was opened: nothing to correct, so nothing is sent.
        if (Object.keys(parsed.changes).length === 0) {
            onHide()
            return
        }
        mutate(
            { moneyPool: id, poolShip: user.id, changes: parsed.changes },
            {
                onSuccess: onHide,
                onError: (refused: IdentityRefusal) => {
                    if (refused?.code === 2053)
                        setRefusedReason(refused.param ?? 'unknown')
                    else if (refused?.code === 2051)
                        setError(inputError(refused.param))
                    else if (refused?.code === 2054)
                        setError('این عضو در صندوق پیدا نشد.')
                    else setError(SAVE_FAILED)
                },
            }
        )
    }

    const blocked =
        refusedReason ??
        (data && !data.editable ? data.reason ?? 'unknown' : undefined)

    return (
        <div className=" w-full text-center py-4 text-sm px-4">
            <p className=" mb-3">
                اصلاح نام و شمارهٔ {user.member.first_name}{' '}
                {user.member.last_name}
            </p>
            <hr className=" mb-3" />
            {isLoading && <DotsLoading />}
            {isError && (
                <>
                    <p role="alert" className=" text-xs leading-5 text-gray-1">
                        اطلاعات این عضو دریافت نشد.
                    </p>
                    <hr className=" my-3" />
                    <p className=" text-green-1" onClick={() => ask()}>
                        تلاش دوباره
                    </p>
                </>
            )}
            {blocked && (
                <p className=" text-xs leading-6 text-gray-1 text-right">
                    {blockMessage(blocked)}
                </p>
            )}
            {data && form && !blocked && (
                <>
                    <p className=" text-xs leading-5 text-gray-1 text-right">
                        این عضو هنوز وارد همیان نشده است. اگر نام یا شماره‌اش
                        اشتباه ثبت شده، همین‌جا اصلاح کنید.
                    </p>
                    <label className={LABEL} htmlFor="member-identity-first">
                        نام
                    </label>
                    <input
                        id="member-identity-first"
                        type="text"
                        className={INPUT}
                        value={form.firstName}
                        maxLength={100}
                        onChange={(e) => set('firstName')(e.target.value)}
                    />
                    <label className={LABEL} htmlFor="member-identity-last">
                        نام خانوادگی
                    </label>
                    <input
                        id="member-identity-last"
                        type="text"
                        className={INPUT}
                        value={form.lastName}
                        maxLength={100}
                        onChange={(e) => set('lastName')(e.target.value)}
                    />
                    <label className={LABEL} htmlFor="member-identity-phone">
                        شماره موبایل
                    </label>
                    <input
                        id="member-identity-phone"
                        type="text"
                        inputMode="tel"
                        dir="ltr"
                        className={INPUT}
                        value={form.phone}
                        placeholder="09123456789"
                        onChange={(e) => set('phone')(e.target.value)}
                    />
                    <p className=" mt-2 text-xs leading-5 text-gray-1 text-right">
                        برای شمارهٔ جدید پیامکی فرستاده نمی‌شود؛ پیام‌های بعدی
                        صندوق به همین شماره می‌رسد.
                    </p>
                    {!!error && (
                        <p
                            role="alert"
                            className=" mt-2 text-xs leading-5 text-danger-on"
                        >
                            {error}
                        </p>
                    )}
                    <hr className=" my-3" />
                    <p
                        className={isSaving ? ' text-gray-1' : ' text-green-1'}
                        onClick={save}
                    >
                        {isSaving ? 'در حال ذخیره…' : 'ذخیره'}
                    </p>
                </>
            )}
            <hr className=" my-3" />
            <p className=" my-2" onClick={onHide}>
                بستن
            </p>
        </div>
    )
}
